import React, { useEffect, useState } from 'react';
import {
    CModal,
    CModalHeader,
    CModalTitle,
    CModalBody,
    CModalFooter,
    CButton,
    CSpinner,
    CRow,
    CCol,
    CAvatar,
    CFormInput,
    CAlert,
} from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import { cilBasket, cilLocationPin, cilPhone, cilUser, cilClock, cilCheckCircle } from '@coreui/icons';
import * as DomicilappService from '../../services/DomicilappService';
import { StoreOrderDetail } from '../../types/order';
import { showToastTR } from '../../Utils/BaseApp';
import {
    DELIVERY_TYPES,
    DeliveryTypeBadge,
    formatCurrency,
    formatDateTime,
    getApiErrorMessage,
    whatsappLink,
} from './orderUtils';

interface OrderDetailModalProps {
    storeId: string;
    orderId: number | null;
    onClose: () => void;
    onStatusChanged: () => void; // para que el listado se recargue
}

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <span className="text-uppercase text-body-secondary fw-bold d-block mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
        {children}
    </span>
);

export const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ storeId, orderId, onClose, onStatusChanged }) => {
    const [order, setOrder] = useState<StoreOrderDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);

    const loadOrder = (id: number) => {
        setLoading(true);
        setError(null);
        DomicilappService.getOrderByStore(storeId, id)
            .then((response) => {
                if (response?.status === 200 && response.data) {
                    setOrder(response.data);
                } else {
                    setError(getApiErrorMessage(response, 'No fue posible cargar la orden'));
                }
            })
            .catch((err) => setError(getApiErrorMessage(err, 'No fue posible cargar la orden')))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        setOrder(null);
        setCode('');
        if (orderId) loadOrder(orderId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderId, storeId]);

    const nextStatus = order?.next_status ?? null;
    // Entregar un pedido para recoger exige el código que muestra el cliente
    const requiresCode = !!nextStatus && Number(nextStatus.id) === Number(order?.final_status_id) && Number(order?.delivery_type_id) === DELIVERY_TYPES.PICKUP;

    const advanceStatus = () => {
        if (!order || !nextStatus) return;
        if (requiresCode && !code.trim()) {
            setError('Ingresa el código de la orden que presenta el cliente');
            return;
        }
        setUpdating(true);
        setError(null);
        DomicilappService.updateOrderStatusByStore(storeId, order.id, { status_id: nextStatus.id, code: requiresCode ? code.trim() : undefined })
            .then((response) => {
                if (response?.status === 200) {
                    showToastTR(`Orden #${order.id}: ${nextStatus.name}`, 2500, 'success');
                    setCode('');
                    onStatusChanged();
                    loadOrder(order.id);
                } else {
                    setError(getApiErrorMessage(response, 'No fue posible actualizar la orden'));
                }
            })
            .catch((err) => setError(getApiErrorMessage(err, 'No fue posible actualizar la orden')))
            .finally(() => setUpdating(false));
    };

    const subtotal = order?.products.reduce((acc, p) => acc + p.total_price, 0) ?? 0;

    return (
        <CModal visible={orderId !== null} onClose={onClose} size="lg" alignment="center" scrollable>
            <CModalHeader>
                <CModalTitle className="d-flex align-items-center gap-2">
                    <span>Orden #{orderId}</span>
                    {order && <DeliveryTypeBadge typeId={order.delivery_type_id} name={order.delivery_type_name} />}
                </CModalTitle>
            </CModalHeader>

            <CModalBody>
                {loading && !order ? (
                    <div className="text-center py-5">
                        <CSpinner color="primary" size="sm" className="me-2" />
                        <span className="text-body-secondary">Cargando orden...</span>
                    </div>
                ) : !order ? (
                    error && <CAlert color="danger" className="mb-0">{error}</CAlert>
                ) : (
                    <>
                        {error && <CAlert color="danger">{error}</CAlert>}

                        {/* RIESGO DE FRAUDE: solo se avisa en medio y alto */}
                        {order.risk && order.risk.level !== 'low' && (
                            <CAlert color={order.risk.level === 'high' ? 'danger' : 'warning'} className="small">
                                <strong>{order.risk.level === 'high' ? 'Pedido de riesgo alto' : 'Pedido para revisar'}</strong>
                                {` · ${order.risk.score} puntos. `}Te recomendamos llamar al cliente para confirmar antes de prepararlo.
                                <ul className="mb-0 mt-1 ps-3">
                                    {order.risk.reasons.map((reason) => (
                                        <li key={reason.label}>
                                            {reason.label} <span className="opacity-75">(+{reason.points})</span>
                                        </li>
                                    ))}
                                </ul>
                            </CAlert>
                        )}

                        {/* CLIENTE Y ENTREGA */}
                        <CRow className="g-3 mb-4">
                            <CCol md={6}>
                                <div className="p-3 rounded-3 bg-body-tertiary h-100">
                                    <SectionTitle>Cliente</SectionTitle>
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        {order.user_profile_image ? (
                                            <CAvatar src={order.user_profile_image} size="md" />
                                        ) : (
                                            <CAvatar color="secondary" textColor="white" size="md"><CIcon icon={cilUser} /></CAvatar>
                                        )}
                                        <div className="fw-semibold text-body">{order.user_full_name}</div>
                                    </div>
                                    {order.user_phone_number && (
                                        <div className="small d-flex align-items-center gap-2">
                                            <CIcon icon={cilPhone} size="sm" className="text-body-secondary" />
                                            <a href={whatsappLink(order.user_phone_number)} target="_blank" rel="noreferrer">
                                                {order.user_phone_number}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </CCol>
                            <CCol md={6}>
                                <div className="p-3 rounded-3 bg-body-tertiary h-100">
                                    <SectionTitle>Entrega</SectionTitle>
                                    <div className="small d-flex align-items-start gap-2 mb-1">
                                        <CIcon icon={cilLocationPin} size="sm" className="text-body-secondary mt-1" />
                                        <span className="text-body">
                                            {Number(order.delivery_type_id) === DELIVERY_TYPES.PICKUP ? 'El cliente recoge en la tienda' : order.address ?? 'Sin dirección'}
                                        </span>
                                    </div>
                                    {order.delivery_date && (
                                        <div className="small d-flex align-items-center gap-2 mb-1">
                                            <CIcon icon={cilClock} size="sm" className="text-body-secondary" />
                                            <span className="text-body">Programado: {formatDateTime(order.delivery_date)}</span>
                                        </div>
                                    )}
                                    <div className="small text-body-secondary mt-2">
                                        Código de la orden: <span className="fw-bold text-body font-monospace">{order.code}</span>
                                    </div>
                                </div>
                            </CCol>
                        </CRow>

                        {/* PRODUCTOS */}
                        <SectionTitle>Productos</SectionTitle>
                        <div className="border rounded-3 mb-4">
                            {order.products.map((product, index) => (
                                <div key={index} className={`d-flex gap-3 p-3 ${index > 0 ? 'border-top' : ''}`}>
                                    {product.image ? (
                                        <CAvatar src={product.image} shape="rounded" size="md" />
                                    ) : (
                                        <div className="rounded d-flex align-items-center justify-content-center text-body-secondary bg-body-tertiary" style={{ width: 40, height: 40 }}>
                                            <CIcon icon={cilBasket} />
                                        </div>
                                    )}
                                    <div className="flex-grow-1">
                                        <div className="fw-semibold text-body">
                                            {product.quantity} × {product.product_name}
                                            {product.variant_name && <span className="text-body-secondary fw-normal"> ({product.variant_name})</span>}
                                            {product.is_gift && <span className="badge bg-success-subtle text-success border border-success-subtle ms-2 fw-normal">Regalo de promoción</span>}
                                        </div>
                                        {product.toppings.length > 0 && (
                                            <div className="small text-body-secondary">
                                                {product.toppings.map((t) => `${t.quantity > 1 ? t.quantity + '× ' : ''}${t.name}`).join(', ')}
                                            </div>
                                        )}
                                        {product.addons.length > 0 && (
                                            <div className="small text-body-secondary">
                                                + {product.addons.map((a) => `${a.quantity > 1 ? a.quantity + '× ' : ''}${a.name}`).join(', ')}
                                            </div>
                                        )}
                                    </div>
                                    <div className="fw-semibold text-body text-nowrap">{formatCurrency(product.total_price)}</div>
                                </div>
                            ))}
                        </div>

                        <CRow className="g-3">
                            {/* HISTORIAL */}
                            <CCol md={6}>
                                <SectionTitle>Seguimiento</SectionTitle>
                                <ul className="list-unstyled mb-0">
                                    {order.order_history.map((step) => {
                                        const done = !!step.created_at;
                                        return (
                                            <li key={step.id} className="d-flex align-items-start gap-2 mb-2">
                                                <CIcon
                                                    icon={cilCheckCircle}
                                                    className={done ? 'text-success mt-1' : 'text-body-secondary opacity-50 mt-1'}
                                                />
                                                <div>
                                                    <div className={`small fw-semibold ${done ? 'text-body' : 'text-body-secondary'}`} style={{ textTransform: 'capitalize' }}>
                                                        {step.order_status_name}
                                                    </div>
                                                    {done && <div className="text-body-secondary" style={{ fontSize: '0.75rem' }}>{formatDateTime(step.created_at)}</div>}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </CCol>

                            {/* TOTALES */}
                            <CCol md={6}>
                                <SectionTitle>Resumen de pago</SectionTitle>
                                <div className="small">
                                    <div className="d-flex justify-content-between mb-1">
                                        <span className="text-body-secondary">Productos</span>
                                        <span className="text-body">{formatCurrency(order.cart_price || subtotal)}</span>
                                    </div>
                                    {Number(order.promotions_discount) > 0 && (
                                        <div className="d-flex justify-content-between mb-1">
                                            <span className="text-body-secondary">Promociones</span>
                                            <span className="text-success">− {formatCurrency(order.promotions_discount ?? 0)}</span>
                                        </div>
                                    )}
                                    {Number(order.coupon_discount) > 0 && (
                                        <div className="d-flex justify-content-between mb-1">
                                            <span className="text-body-secondary">Cupón {order.coupon_code}</span>
                                            <span className="text-success">− {formatCurrency(order.coupon_discount)}</span>
                                        </div>
                                    )}
                                    <div className="d-flex justify-content-between mb-1">
                                        <span className="text-body-secondary">Domicilio</span>
                                        <span className="text-body">{formatCurrency(order.delivery_price)}</span>
                                    </div>
                                    {Number(order.packaging_fee) > 0 && (
                                        <div className="d-flex justify-content-between mb-1">
                                            <span className="text-body-secondary">Empaque</span>
                                            <span className="text-body">{formatCurrency(order.packaging_fee ?? 0)}</span>
                                        </div>
                                    )}
                                    {Number(order.applied_balance) > 0 && (
                                        <div className="d-flex justify-content-between mb-1">
                                            <span className="text-body-secondary">Pagado con saldo</span>
                                            <span className="text-success">− {formatCurrency(order.applied_balance)}</span>
                                        </div>
                                    )}
                                    <div className="d-flex justify-content-between border-top pt-2 mt-2 fw-bold fs-6">
                                        <span className="text-body">Total a cobrar</span>
                                        <span className="text-body">{formatCurrency(order.total_price - (order.applied_balance || 0))}</span>
                                    </div>
                                </div>
                            </CCol>
                        </CRow>
                    </>
                )}
            </CModalBody>

            <CModalFooter className="d-flex justify-content-between">
                {order && nextStatus ? (
                    <div className="d-flex align-items-center gap-2 flex-grow-1 justify-content-end">
                        {requiresCode && (
                            <CFormInput
                                placeholder="Código del cliente"
                                value={code}
                                onChange={(e) => setCode(e.target.value.toUpperCase())}
                                style={{ maxWidth: 200 }}
                                className="font-monospace"
                            />
                        )}
                        <CButton color="light" onClick={onClose}>Cerrar</CButton>
                        <CButton
                            className="text-white fw-semibold"
                            style={{ backgroundColor: '#FE2445', borderColor: '#FE2445' }}
                            disabled={updating}
                            onClick={advanceStatus}
                        >
                            {updating && <CSpinner size="sm" className="me-2" />}
                            Pasar a "{nextStatus.name}"
                        </CButton>
                    </div>
                ) : (
                    <div className="d-flex w-100 justify-content-end">
                        <CButton color="light" onClick={onClose}>Cerrar</CButton>
                    </div>
                )}
            </CModalFooter>
        </CModal>
    );
};

export default OrderDetailModal;
